import { queryOptions } from '@tanstack/react-query'

import type { User } from '../dal/user-dal'
import { fetchUser } from '../dal/user-dal'
import {
  isMissingAccessTokenError,
  MissingAccessTokenError,
} from '../utils/auth-errors'
import { hasHttpStatus } from '../utils/parseApiError'
import { addBreadcrumb } from '../utils/sentry'
import { queryKeys } from './query-keys'

export type { User } from '../dal/user-dal'

function normalizeUserCreatedDate(user: null): null
function normalizeUserCreatedDate(user: User): User
function normalizeUserCreatedDate(user: User | null): User | null
function normalizeUserCreatedDate(user: User | null): User | null {
  if (user == null) {
    return null
  }

  const created = user.created as Date | string
  if (created instanceof Date) {
    return user
  }
  if (typeof created === 'string') {
    return { ...user, created: new Date(created) }
  }
  return user
}

const REVENUECAT_ID_POLL_INTERVAL_MS = 500
const REVENUECAT_ID_MAX_ATTEMPTS = 20

class RevenueCatIdMissingError extends Error {
  constructor() {
    super('RevenueCat ID not yet available')
    this.name = 'RevenueCatIdMissingError'
  }
}

function hasRevenueCatId(user: User): boolean {
  return user.revenueCatId != null && user.revenueCatId.trim().length > 0
}

/**
 * Primary user profile query used throughout the app (dashboard, settings, etc.).
 *
 * - Uses cached data (`fetchUser(false)`) with a 5-minute staleTime.
 * - Always refetches on mount so persisted/hydrated profile state cannot
 *   keep routing decisions such as post-payment onboarding stale after reloads.
 * - Accepts partially provisioned backend users during signup.
 * - Returns null on 404 because an Auth0 user can exist before the backend user.
 *
 * Cannot be consolidated with the other user queries because:
 * - `userWithRevenueCatIdQueryOptions` forces a backend sync and is designed for
 *   imperative one-shot polling during post-payment setup.
 * - `userEntitlementSyncQueryOptions` forces a backend sync without any RevenueCat ID
 *   validation, serving a fundamentally different purpose (entitlement verification).
 */
export const userQueryOptions = (
  authUserId: string | null,
  getAccessToken?: () => Promise<string | null>,
) =>
  queryOptions({
    queryKey: queryKeys.user.profile(authUserId),
    queryFn: async () => {
      if (getAccessToken != null) {
        const token = await getAccessToken()
        if (token == null) {
          throw new MissingAccessTokenError()
        }
      }
      let user: User
      try {
        user = await fetchUser(false)
      } catch (error) {
        if (hasHttpStatus(error, 404)) {
          return null
        }
        throw error
      }
      if (hasRevenueCatId(user)) {
        addBreadcrumb('purchase', 'revenuecat_id_ready', {
          revenueCatId: user.revenueCatId,
        })
      }
      return user
    },
    staleTime: 5 * 60 * 1000,
    select: (user: User | null) => normalizeUserCreatedDate(user),
    refetchOnMount: 'always',
    retry: (failureCount, error) => {
      if (isMissingAccessTokenError(error)) return false
      return failureCount < 2
    },
    retryDelay: (failureCount, error) => {
      return Math.min(1000 * 2 ** failureCount, 30000)
    },
    retryOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  })

/**
 * Polls for RevenueCat ID availability during post-payment onboarding setup.
 *
 * - Forces a backend sync (`fetchUser(true)`) on every attempt.
 * - Retries up to 20 times at 500ms intervals until the RevenueCat ID appears.
 * - `enabled: false` — meant to be triggered imperatively via `queryClient.fetchQuery`.
 * - `gcTime: 0` — result is discarded immediately after use.
 *
 * Cannot be consolidated with the other user queries because:
 * - `userQueryOptions` uses cached data and serves as the long-lived profile cache.
 * - `userEntitlementSyncQueryOptions` doesn't validate RevenueCat ID presence at all;
 *   it only needs the raw user/entitlement payload.
 */
export const userWithRevenueCatIdQueryOptions = (authUserId: string | null) =>
  queryOptions({
    queryKey: queryKeys.user.profileWithRevenueCatId(authUserId),
    queryFn: async () => {
      const user = await fetchUser(true)
      if (!hasRevenueCatId(user)) {
        throw new RevenueCatIdMissingError()
      }
      addBreadcrumb('setup', 'revenuecat_id_ready', {
        revenueCatId: user.revenueCatId,
      })
      return user as User & { revenueCatId: string }
    },
    retry: REVENUECAT_ID_MAX_ATTEMPTS - 1,
    retryDelay: REVENUECAT_ID_POLL_INTERVAL_MS,
    enabled: false,
    staleTime: 0,
    gcTime: 0,
    select: (user) =>
      normalizeUserCreatedDate(user) as User & { revenueCatId: string },
  })

/**
 * Fetches a fresh user profile with backend sync for entitlement verification.
 *
 * - Forces a backend sync (`fetchUser(true)`) to get the latest entitlement state.
 * - `staleTime: 0` — always fetches fresh data, never serves from cache.
 * - No RevenueCat ID validation — only the entitlement payload matters here.
 * - Uses a dedicated `profileSync` cache key to avoid polluting the main profile cache.
 *
 * Cannot be consolidated with the other user queries because:
 * - `userQueryOptions` serves the long-lived profile cache without blocking on
 *   RevenueCat propagation, which may still be in flight here.
 * - `userWithRevenueCatIdQueryOptions` requires a RevenueCat ID and has aggressive
 *   polling retry logic that would cause unnecessary failures here.
 */
export const userEntitlementSyncQueryOptions = (authUserId: string | null) =>
  queryOptions({
    queryKey: queryKeys.user.profileSync(authUserId),
    queryFn: () => fetchUser(true),
    staleTime: 0,
    select: normalizeUserCreatedDate,
  })
